/* eslint-disable no-restricted-globals */
import React, { useEffect, useState } from "react";
import Navbar from "../components/navbar.jsx";
import Footer from "../components/footer.jsx";
import { CLINICS } from "../pages/clinicsContent.js";

export default function Clinics() {
    const initialSlug = (() => {
        if (typeof window === "undefined") return "mumbai";
        const seg = window.location.pathname.split("/").filter(Boolean);
        return seg[1] || "mumbai";
    })();

    const [clinics, setClinics] = useState(() => (CLINICS && CLINICS.length ? CLINICS : []));
    const [activeSlug, setActiveSlug] = useState(initialSlug);
    const [loading, setLoading] = useState(() => (CLINICS && CLINICS.length ? false : true));

    useEffect(() => {
        let mounted = true;

        if (CLINICS && CLINICS.length) {
            setClinics(CLINICS);
            setLoading(false);
        } else {
            const load = async () => {
                try {
                    const res = await fetch("/api/clinics");
                    if (!res.ok) throw new Error("Failed to load clinics");
                    const data = await res.json();
                    if (!mounted) return;
                    setClinics(data);
                    setLoading(false);
                } catch (err) {
                    console.error(err);
                    setClinics([]);
                    setLoading(false);
                }
            };
            load();
        }

        const sync = () => {
            if (typeof window === "undefined") return;
            const seg = window.location.pathname.split("/").filter(Boolean);
            const slug = seg[1] || (clinics[0] && clinics[0].slug) || "mumbai";
            setActiveSlug(slug);
        };

        window.addEventListener("locationchange", sync);
        window.addEventListener("popstate", sync);

        return () => {
            mounted = false;
            window.removeEventListener("locationchange", sync);
            window.removeEventListener("popstate", sync);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Scroll into view whenever activeSlug changes and the element exists.
    useEffect(() => {
        if (loading) return;
        if (!activeSlug) return;

        // Small delay to allow rendering when necessary
        const t = setTimeout(() => {
            const el = document.getElementById(activeSlug);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 80);

        return () => clearTimeout(t);
    }, [activeSlug, loading, clinics]);

    const handleNavigate = (slug) => {
        const href = `/clinics/${slug}`;
        if (typeof window !== "undefined" && window.history && window.history.pushState) {
            window.history.pushState({}, "", href);
            window.dispatchEvent(new Event("locationchange"));
        } else {
            // fallback
            window.location.href = href;
        }
        setActiveSlug(slug);
        // scrolling handled by effect above
    };

    return (
        <>
            <Navbar />
            <main className="pt-24 pb-16 bg-emerald-50 min-h-screen">
                <div className="max-w-7xl mx-auto px-6">
                    <header className="rounded-2xl bg-gradient-to-r from-emerald-700 to-emerald-600 text-white p-10 md:p-16 shadow-lg">
                        <h1 className="text-3xl md:text-4xl font-extrabold">Nexus Clinics</h1>
                        <p className="mt-4 text-emerald-100 max-w-3xl">
                            Regional clinics across India offering psychiatry, therapy, assessments and specialist programs.
                        </p>
                    </header>

                    <div className="mt-10 grid gap-8 lg:grid-cols-[300px_1fr]">
                        <aside className="sticky top-28 hidden lg:block">
                            <div className="rounded-xl bg-white p-5 shadow ring-1 ring-emerald-100">
                                <h4 className="font-semibold text-emerald-800">Our Clinics</h4>
                                <nav className="mt-3 flex flex-col gap-2 text-sm">
                                    {loading ? (
                                        <p className="text-slate-600">Loading...</p>
                                    ) : (
                                        clinics.map((c) => (
                                            <button
                                                key={c.slug}
                                                onClick={() => handleNavigate(c.slug)}
                                                className={`w-full text-left px-3 py-2 rounded-lg transition ${activeSlug === c.slug ? "bg-emerald-700 text-white font-semibold" : "text-slate-700 hover:bg-emerald-50"
                                                    }`}
                                            >
                                                {c.title}
                                            </button>
                                        ))
                                    )}
                                </nav>
                            </div>
                        </aside>

                        <div className="space-y-8">
                            {loading && (
                                <div className="rounded-xl bg-white p-6 shadow">
                                    <p>Loading clinics...</p>
                                </div>
                            )}

                            {!loading &&
                                clinics.map((c) => (
                                    <section
                                        id={c.slug}
                                        key={c.slug}
                                        className={`rounded-xl bg-white p-6 shadow ${activeSlug === c.slug ? "ring-2 ring-emerald-200" : ""}`}
                                    >
                                        <div className="flex items-start justify-between gap-6">
                                            <div className="flex-1">
                                                <h2 className="text-2xl font-bold text-emerald-800">{c.title}</h2>
                                                {c.description && c.description.map((p, i) => (
                                                    <p key={i} className="mt-2 text-slate-700">{p}</p>
                                                ))}

                                                <div className="mt-4 space-y-2 text-sm text-slate-700">
                                                    <p><strong>Address:</strong> {c.address}</p>
                                                    <p><strong>Phone:</strong> {c.phone}</p>
                                                    <p><strong>Hours:</strong> {c.hours}</p>
                                                </div>

                                                <div className="mt-4">
                                                    <h4 className="font-semibold text-slate-800">Services offered</h4>
                                                    <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                                                        {c.services.map((s) => <li key={s}>{s}</li>)}
                                                    </ul>
                                                </div>

                                                <div className="mt-6 flex gap-3">
                                                    <a href={`/clinics/${c.slug}/book`} className="inline-flex items-center rounded-full bg-emerald-700 px-5 py-2 text-white font-semibold">Book Appointment</a>
                                                    <button onClick={() => handleNavigate(c.slug)} className="inline-flex items-center rounded-full border px-4 py-2">View details</button>
                                                </div>
                                            </div>

                                            <div className="w-40 hidden sm:block">
                                                <div className="h-36 w-36 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-semibold">
                                                    {c.title.split(" ")[1] || c.title}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                ))}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}